// Constants for the "send this to the dev team" flow in the Key AI chat.
//
// The backend (aiService `send_dev_request` tool -> devRequestService) writes one
// ActivityLogs row plus a JSON context file in S3; the admin-only /devrequests page
// reads them back. Both sides key off the constants here so the type string and the
// S3 prefix can never drift apart.
//
// CommonJS on purpose: doctivity-rest requires this file directly (see the other
// dual-consumed utils, e.g. EncounterTypeUtil), while the admin webpack build picks
// it up through utils/index.js.

const CATEGORIES = {
  ERROR: 'error',
  WRONG_RESULTS: 'wrong_results',
  STUCK: 'stuck',
  FEATURE_REQUEST: 'feature_request',
  OTHER: 'other',
};

const CATEGORY_LABELS = {
  [CATEGORIES.ERROR]: 'Error / crash',
  [CATEGORIES.WRONG_RESULTS]: 'Wrong or unexpected results',
  [CATEGORIES.STUCK]: 'Stuck / needs help',
  [CATEGORIES.FEATURE_REQUEST]: 'Feature request',
  [CATEGORIES.OTHER]: 'Other',
};

// Triage workflow fields, admin-editable on the /devrequests page. These are REAL columns
// on ActivityLogs (added by migration 20260803...-add-devrequest-status-priority) — not
// part of the JSON `context` blob — so the page can sort and filter on them in SQL. The
// stored `status` value is the lowercase string here (e.g. 'in progress'), not the label.
const STATUSES = {
  NEW: 'new',
  IN_PROGRESS: 'in progress',
  COMPLETE: 'complete',
  ARCHIVED: 'archived',
};

const STATUS_LABELS = {
  [STATUSES.NEW]: 'New',
  [STATUSES.IN_PROGRESS]: 'In Progress',
  [STATUSES.COMPLETE]: 'Complete',
  [STATUSES.ARCHIVED]: 'Archived',
};

// Display order for dropdowns (roughly the lifecycle order).
const STATUS_ORDER = [
  STATUSES.NEW,
  STATUSES.IN_PROGRESS,
  STATUSES.COMPLETE,
  STATUSES.ARCHIVED,
];

// The "working" statuses — everything except archived. Used as the page's default filter.
const ACTIVE_STATUSES = [STATUSES.NEW, STATUSES.IN_PROGRESS, STATUSES.COMPLETE];

// 1 (highest) .. 5 (lowest).
const PRIORITIES = [1, 2, 3, 4, 5];

// Defaults for a newly submitted request (see devRequestService.submit).
const DEFAULT_STATUS = STATUSES.NEW;
const DEFAULT_PRIORITY = 5;

const DevRequestUtil = {
  // ActivityLogs.type — free-form STRING(64) in this codebase; there is no central
  // enum of activity types, so this constant is the closest thing to one.
  ACTIVITY_TYPE: 'AI_DEVREQUEST',

  // ActivityLogs.model — display grouping only, not a Sequelize model name.
  ACTIVITY_MODEL: 'Dev Request',

  // Key prefix inside UPLOADS_BUCKET_NAME. The download endpoint REQUIRES keys to
  // start with this, so it doubles as a security boundary — do not reuse it for
  // anything else, and do not relax the check in handler.js.
  S3_PREFIX: 'devrequests',

  CATEGORIES,
  CATEGORY_LABELS,

  STATUSES,
  STATUS_LABELS,
  STATUS_ORDER,
  ACTIVE_STATUSES,
  PRIORITIES,
  DEFAULT_STATUS,
  DEFAULT_PRIORITY,

  isValidCategory(category) {
    return Object.values(CATEGORIES).includes(category);
  },

  categoryLabel(category) {
    return CATEGORY_LABELS[category] || CATEGORY_LABELS[CATEGORIES.OTHER];
  },

  isValidStatus(status) {
    return Object.values(STATUSES).includes(status);
  },

  statusLabel(status) {
    return STATUS_LABELS[status] || status || '';
  },

  isValidPriority(priority) {
    return PRIORITIES.includes(Number(priority));
  },
};

module.exports = { DevRequestUtil };
